import {SyntheticEvent, useCallback, useEffect, useRef, useState} from 'react';
import {twMerge} from '../../../common/twMerge';
import {TalkMediaStatus} from '../../../domain/content/[seriesId]/viewer/[productId]/type/TalkMediaStatus.enum';
import {TalkShapeType} from '../../../domain/content/[seriesId]/viewer/[productId]/type/TalkShapeType.enum';
import {TalkType} from '../../../domain/content/[seriesId]/viewer/[productId]/type/TalkType.enum';
import {formatTalkViewerMediaTime} from '../../../domain/content/[seriesId]/viewer/[productId]/util/formatTalkViewerMediaTime';
import {setMediaCurrentTime} from '../../../domain/content/[seriesId]/viewer/[productId]/util/setMediaCurrentTime';
import {Text} from '../../atoms/Text/Text';
import {TalkShape} from '../TalkShape/TalkShape';
import Equalizer from './Equalizer';
import {TalkAudioProps} from './TalkAudioProps.interface';

export function TalkAudio({
  media,
  mediaStatus,
  textColor,
  showEqualizer,
  muted,
  needMediaPlayedDirect,
  onEndedAudio,
  talkColor,
  talkPosition,
  talkShapeType,
  talkType,
  talkBorderColor,
  characterName,
  characterNameColor,
  hasTalkTail,
  className,
}: TalkAudioProps) {
  const audioRef = useRef<HTMLAudioElement>(null);
  const {
    source: {totalTime = 0, url},
  } = media;
  const [remainTime, setRemainTime] = useState(totalTime);
  useEffect(() => {
    if (!audioRef.current || mediaStatus === undefined) {
      return;
    }
    if (mediaStatus === TalkMediaStatus.Play) {
      if (needMediaPlayedDirect) {
        return;
      }
      audioRef.current.play();
    } else if (mediaStatus === TalkMediaStatus.Stop) {
      audioRef.current.pause();
      setMediaCurrentTime(audioRef.current, 0);
    }
  }, [audioRef, mediaStatus, needMediaPlayedDirect]);

  const timeUpdateHandler = useCallback((e: SyntheticEvent) => {
    const audio = e.target as HTMLAudioElement;
    const currentRemainTime = audio.duration - audio.currentTime;
    setRemainTime(currentRemainTime);
  }, []);

  useEffect(() => {
    if (!audioRef.current) {
      return;
    }
    audioRef.current.muted = muted;
  }, [muted]);

  const isPlaying = mediaStatus === TalkMediaStatus.Play;
  const showingTime = isPlaying ? remainTime : totalTime;

  const onClickSound = useCallback(() => {
    if (!audioRef.current || muted || !needMediaPlayedDirect) {
      return;
    }
    audioRef.current.paused && audioRef.current.play();
  }, [muted, needMediaPlayedDirect]);
  return (
    <TalkShape
      talkShapeType={talkShapeType}
      characterName={characterName}
      characterNameColor={characterNameColor}
      talkPosition={talkPosition}
      talkColor={talkColor}
      talkBorderColor={talkBorderColor}
      hasTalkTail={hasTalkTail}
      onClick={onClickSound}
      className={twMerge(
        talkShapeType === TalkShapeType.Bubble &&
          BUBBLE_TALK_TYPE_STYLE[talkType],
        className
      )}
    >
      <div className="flex items-center">
        <audio
          ref={audioRef}
          controls={false}
          src={url}
          onTimeUpdate={timeUpdateHandler}
          onEnded={onEndedAudio}
          /**
           * 톡뷰어는 첫 렌더에 다수의 미디어 파일이 렌더될 수 있으므로, 여기에 preload 속성을 주게 되면
           * 한번에 많은 요청 연결이 생기게 될 가능성이 있고, 이 때 에러를 발생해 특정 기기에서 미디어의 정상적인 재생을 불가능하게 만든다.
           * 따라서 톡뷰어에 사용하는 미디어는 preload 속성을 모두 제거한다.
           */
          preload="none"
        />
        <svg
          xmlns="http://www.w3.org/2000/svg"
          viewBox="0 0 28 28"
          className="ml-2tvs h-22tvs w-22tvs"
        >
          <g fill={textColor} fillRule="evenodd">
            {!isPlaying && (
              <path d="M23.134 15.645L7.138 26.707A2 2 0 0 1 4 25.062V2.952a2 2 0 0 1 3.137-1.646l15.996 11.048a2 2 0 0 1 0 3.29z" />
            )}
            {isPlaying && <rect width="22" height="22" x="3" y="3" rx="2" />}
          </g>
        </svg>

        {showEqualizer && (
          <Equalizer
            isPlaying={isPlaying}
            color={textColor}
            className="ml-12tvs h-38tvs w-83tvs"
          />
        )}
        {!showEqualizer && (
          <Text className="ml-12tvs min-w-76tvs text-30tvs">
            {formatTalkViewerMediaTime(showingTime)}
          </Text>
        )}
      </div>
    </TalkShape>
  );
}

const BUBBLE_TALK_TYPE_STYLE: {[key in TalkType]: string} = {
  [TalkType.Narration]: 'max-w-660tvs font-medium',
  [TalkType.Omniscient]:
    'max-w-567tvs min-w-110tvs px-29tvs py-22tvs rounded-40tvs',
  [TalkType.Talker]:
    'rounded-26tvs max-w-474tvs min-w-78tvs px-26tvs pt-19.5tvs pb-20.5tvs',
  [TalkType.None]: '',
};
